import React, { useState } from 'react';
import { Button, Box, FormControlLabel, FormControl } from '@mui/material';
import DialogModal from '../../../components/DialogModal';
import DropdownSelect from '../../../components/DropdownSelect/DropdownSelect';
import { TransformedStudentDetail } from '../../../types/userType';
import paymentSchemesApis from '../../../apis/payments/paymentSchemes';
import OverlayLoader from '../../../components/OverlayLoader/OverlayLoader';
import { SnackbarSeverityType } from '../../../enums';
import { academicYears } from '../../../constants/academicYears';
import { useCallback, useEffect } from 'react';
import pallet from '../../../layouts/pallet';
import { StyledCheckbox } from '../styled';

interface FirstPaymentLinkDialogProps {
  open: boolean;
  onClose: () => void;
  userDetails: TransformedStudentDetail | null;
  onShowToast: (message: string, severity: SnackbarSeverityType) => void;
}

const FirstPaymentLinkDialog: React.FC<FirstPaymentLinkDialogProps> = ({
  open,
  onClose,
  userDetails,
  onShowToast
}) => {
  const isNewAdmissionStudent =
    userDetails?.studentDetails?.isNewAdmission === true;

  const defaultAcademicYear = academicYears.find((year) => year.isDefault);
  const [academicYear, setAcademicYear] = useState<string>(
    defaultAcademicYear?.value || academicYears[0].value
  );
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(
    isNewAdmissionStudent
  );
  const sendFirstPaymentLink = true;
  const [loading, setLoading] = useState(false);

  const resetState = useCallback(() => {
    setAcademicYear(defaultAcademicYear?.value || academicYears[0].value);
    setSendWelcomeEmail(isNewAdmissionStudent);
    setLoading(false);
  }, [defaultAcademicYear, isNewAdmissionStudent]);

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open, resetState]);

  const isSaveDisabled = !academicYear || !sendFirstPaymentLink || loading;

  const handleSave = async () => {
    if (!userDetails?.studentDetails?.id || !academicYear) {
      console.error(
        'Missing user ID or academic year for sending payment link'
      );
      return;
    }

    setLoading(true);
    try {
      await paymentSchemesApis.sendPaymentLinkPost({
        academicYear,
        userId: userDetails.studentDetails.id,
        sendWelcomeEmail
      });
      onClose();
      resetState();
    } catch (error: any) {
      onShowToast(
        error?.errors?.[0]?.message || 'Failed to send payment link.',
        SnackbarSeverityType.error
      );
    } finally {
      setLoading(false);
    }
  };

  const dialogActions = (
    <Button
      variant='contained'
      onClick={handleSave}
      sx={{ textTransform: 'none' }}
      disabled={isSaveDisabled}
    >
      Send
    </Button>
  );

  return (
    <DialogModal
      open={open}
      onClose={onClose}
      title='First Payment Link'
      maxWidth='sm'
      actions={dialogActions}
    >
      {loading && <OverlayLoader show={loading} />}
      <DropdownSelect
        label='Academic Year'
        placeholder='Select Academic Year'
        value={academicYear}
        options={academicYears}
        onChange={(e) => setAcademicYear(e.target.value as string)}
        name='academicYear'
        required
        fullWidth
        hasTopLabel
      />
      <FormControl component='fieldset'>
        <Box sx={{ display: 'flex', gap: 2, mx: 2, my: 2 }}>
          <FormControlLabel
            control={
              <StyledCheckbox
                isNewAdmissionStudent={isNewAdmissionStudent}
                checked={sendWelcomeEmail}
                onChange={(e) => setSendWelcomeEmail(e.target.checked)}
                disabled={!isNewAdmissionStudent}
              />
            }
            label='Send Welcome Email'
          />
          <FormControlLabel
            control={
              <StyledCheckbox checked={sendFirstPaymentLink} disabled={true} />
            }
            label='Send First Payment Link'
            sx={{
              '& .MuiFormControlLabel-label': {
                color: `${pallet.disabledGrey} !important`
              }
            }}
          />
        </Box>
      </FormControl>
    </DialogModal>
  );
};

export default FirstPaymentLinkDialog;
